import React, { useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { withFormik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Form, Grid, Image } from 'semantic-ui-react';
import styled from 'styled-components';

import { iRootDispatch } from '../../store';
import { UserState } from '../../types/UserState';
import { FormValues } from '../login/types';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { FormikErrorMessage } from '../../components/ErrorMessage';
import { FieldLabel } from '../../styles';
import {
    oAuthSignIn,
    redirectToCapturePage,
    redirectToOAuthLogin,
    checkUserExists,
    verifyExternalLogin,
    confiaxSignIn,
    forgotPassword
} from '../../services/oauth';
import {
    dangerNotification,
    successNotification,
    warningNotification
} from '../../services/notification';
import * as Storage from '../../services/storage';
import { ResponsibleCompanyEnum } from '../../enums/responsible-company.enum';
import logorv from '../../resources/imgs/logo-lado-bom-seguros.svg';

// Styled Components for PWA Login
const PwaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background-color: #f7f7f7;
`;

const PwaLogo = styled(Image)`
  margin-bottom: 40px;
  max-width: 200px;

  @media (max-width: 768px) {
    max-width: 180px;
    display: block;
    margin: 0 auto 40px auto;
  }

  @media (max-width: 480px) {
    max-width: 160px;
    display: block;
    margin: 0 auto 32px auto;
  }
`;

const PwaCard = styled.div`
  background: white;
  padding: 32px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  width: 100%;
  max-width: 400px;
`;

const WelcomeText = styled.h2`
  text-align: center;
  color: #333;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 1.4em;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 1.3em;
    text-align: center;
  }
`;

const mapDispatch = (dispatch: iRootDispatch) => ({
    updateUser: (user: UserState) => dispatch.user.updateUser(user)
});

type Props = ReturnType<typeof mapDispatch> & RouteComponentProps & {
    initialEmail?: string;
    initialPassword?: string;
};

const PwaLoginForm = (props: Props & FormikProps<FormValues> & { isInternalLogin: boolean, hasPassword: boolean, hasEmail?: string }) => {
    const {
        values,
        handleSubmit,
        handleChange,
        isInternalLogin,
        hasPassword
    } = props;

    return (
        <PwaContainer>
            <PwaLogo src={logorv} />
            <PwaCard>
                <WelcomeText>Bem-vindo de volta!</WelcomeText>
                <Form onSubmit={handleSubmit}>
                    <FieldLabel>E-MAIL</FieldLabel>
                    <Input
                        name="email"
                        type="email"
                        icon={{ name: 'users', color: 'blue' }}
                        iconPosition="left"
                        fluid
                        onChange={handleChange}
                        value={values.email}
                        placeholder="Digite seu e-mail"
                    />
                    <FormikErrorMessage component="div" name="email" />

                    <div style={{ height: 16 }} />

                    {isInternalLogin && hasPassword && (
                        <>
                            <FieldLabel>SENHA</FieldLabel>
                            <Input
                                name="password"
                                type="password"
                                icon={{ name: 'lock', color: 'blue' }}
                                iconPosition="left"
                                fluid
                                onChange={handleChange}
                                value={values.password}
                                placeholder="Digite sua senha"
                            />
                            <FormikErrorMessage component="div" name="password" />
                            <div style={{ height: 8 }} />
                            <div style={{ textAlign: 'right' }}>
                                <a onClick={() => props.history.push('/recovery-password')} style={{ cursor: 'pointer', fontSize: '0.9rem' }}>
                                    Esqueci minha senha
                                </a>
                            </div>
                            <div style={{ height: 24 }} />
                        </>
                    )}

                    <Button
                        text={isInternalLogin ? "Entrar" : "Verificar E-mail"}
                        color="green"
                        size="large"
                        type="submit"
                        fluid
                    />
                </Form>
            </PwaCard>
        </PwaContainer>
    );
};

const PwaLogin = (props: Props) => {
    return <PwaLoginController {...props} />;
};

// Refactored Controller for Clarity
const PwaLoginController = (props: Props) => {
    const [isInternalLogin, setIsInternalLogin] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);

    const FormObj = withFormik<Props & { isInternalLogin: boolean, hasPassword: boolean }, FormValues>({
        mapPropsToValues: () => ({ email: '', password: '' }),
        validationSchema: Yup.object().shape({ email: Yup.string().email().required() }),
        handleSubmit: async (values, { setSubmitting }) => {
            const email = values.email.toLowerCase();

            // First Step: Verify
            if (!isInternalLogin && !values.password) {
                const verify = await verifyExternalLogin(email);
                const isConfiaxClient = verify.company === ResponsibleCompanyEnum.confiax;

                if (isConfiaxClient) {
                    redirectToCapturePage();
                    setSubmitting(false);
                    return;
                }

                Storage.set('external_login', verify.isExternal ? 'true' : 'false');

                if (verify.isExternal) {
                    // OAuth
                    const res = await oAuthSignIn(email);
                    if (res) redirectToOAuthLogin(email);
                    else {
                        const check = await checkUserExists(email);
                        if (check.isValid) redirectToOAuthLogin(email);
                        else warningNotification('Erro', check.message || 'Usuário inválido');
                    }
                } else {
                    // Internal
                    setIsInternalLogin(true);
                    setHasPassword(verify.hasPassword);
                    if (!verify.hasPassword) {
                        await forgotPassword(email);
                        successNotification('Sucesso', 'Senha enviada por email');
                    }
                }
                setSubmitting(false);
                return;
            }

            // Second Step: Login
            if (isInternalLogin && values.password) {
                const res = await confiaxSignIn(email, values.password);
                if (res) redirectToCapturePage();
            }
        }
    })(PwaLoginForm);

    return <FormObj {...props} isInternalLogin={isInternalLogin} hasPassword={hasPassword} />;
}

export default connect(null, mapDispatch)(PwaLogin);
